import {
  CaretDownIcon,
  FunnelSimpleIcon,
  SquareLogoIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import classNames from 'classnames';
import React, {
  Fragment,
  FunctionComponent,
  memo,
  useCallback,
  useId,
  useMemo,
  useState,
} from 'react';
import { Field, useFormState } from 'react-final-form';

import { translate } from 'waldur-i18n-runtime';
import { BaseButton, Checkbox, MenuPopover, Radio, Tooltip } from 'waldur-ui';

import { CopyToClipboardButton } from '@/core/CopyToClipboardButton';
import { FieldErrorMessage } from '@/form/FieldError';

import { COLUMN_ACTIONS_KEY } from './constants';
import { TableFilterContext } from './FilterContextProvider';
import { Column, PinnedColumns, PinnedOffsets, TableProps } from './types';
import { getColumnPinKey, getId } from './utils';

interface TableBodyProps extends Pick<
  TableProps,
  | 'rows'
  | 'columns'
  | 'rowClass'
  | 'rowKey'
  | 'expandableRow'
  | 'expandableRowClassName'
  | 'rowActions'
  | 'onRowClick'
  | 'enableMultiSelect'
  | 'selectRow'
  | 'selectedRows'
  | 'toggleRow'
  | 'toggled'
  | 'fetch'
  | 'fieldType'
  | 'fieldName'
  | 'validate'
  | 'rowValidate'
  | 'columnPositions'
  | 'hasOptionalColumns'
  | 'isRowExpandable'
> {
  pinnedColumns?: PinnedColumns;
  pinnedOffsets?: PinnedOffsets;
}

/**
 * Renders a table's `rowActions`. Callers usually pass an inline arrow,
 * which is a new function on every parent render. Used as a component type
 * (`createElement`), that remounted the cell whenever its row re-rendered,
 * which happens after every refetch (window focus, a completed action,
 * polling), and closed any row-action menu the user had open. Calling it as
 * a render function inside this stable component keeps the cell mounted.
 * Hooks it calls belong to RowActionsCell, which is safe because a table
 * always passes the same code. Class, `memo` and `forwardRef` components
 * cannot be called and are module-level constants anyway, so they keep
 * `createElement`.
 */
const RowActionsCell = ({
  rowActions,
  row,
  fetch,
}: {
  rowActions: TableProps['rowActions'];
  row;
  fetch;
}) =>
  typeof rowActions === 'function' && !rowActions.prototype?.isReactComponent
    ? (rowActions as (props: { row; fetch }) => React.ReactNode)({ row, fetch })
    : React.createElement(rowActions, { row, fetch });

interface RowExpander {
  canExpand: boolean;
  isExpanded: boolean;
  /** Toggles the row; the expander button calls it. */
  onToggle: () => void;
  /** Id of the details cell, which the button controls while it is shown. */
  detailsId: string;
}

interface TableCellsProps {
  row;
  columns: TableProps['columns'];
  columnsMap: Record<string, Column>;
  columnPositions: TableProps['columnPositions'];
  hasOptionalColumns: TableProps['hasOptionalColumns'];
  pinnedColumns: PinnedColumns;
  pinnedOffsets: PinnedOffsets;
  expander?: RowExpander;
  hasLeadingCheckbox?: boolean;
}

const InlineFilterButton = memo(({ column, row }: { column: Column; row }) => {
  const { filterComponents, apply, changeFilterValue } =
    React.useContext(TableFilterContext);
  const [open, setOpen] = useState(false);

  const callback = useCallback(() => {
    const filterConfig = filterComponents.find(
      (comp) => comp.name === column.filter,
    );
    const value = column.inlineFilter(row);
    filterConfig.setFilter(value);
    changeFilterValue(column.filter, value);
    apply();
    // Close the popover explicitly on selection, as context apply does
    // not handle popover state.
    setOpen(false);
  }, [filterComponents, column, row, changeFilterValue, apply]);

  return (
    // `.inline-filter-anchor` carries the absolute hover-reveal positioning;
    // the actual `.inline-filter` trigger button stays a normal in-flow element
    // inside it so BaseButton's tooltip wrapper span sizes correctly around it.
    <span className="inline-filter-anchor">
      <MenuPopover open={open} onOpenChange={setOpen} modal={false}>
        <MenuPopover.Trigger asChild>
          <BaseButton
            variant="text-secondary"
            size="sm"
            className="inline-filter icon-align"
            tooltip={translate('Add filter')}
            iconNode={<FunnelSimpleIcon weight="bold" size={20} />}
          />
        </MenuPopover.Trigger>
        <MenuPopover.Content
          align="start"
          className="w-auto min-w-[150px] py-1 font-medium"
        >
          <MenuPopover.Item
            className="px-[16.25px] py-[9.75px]"
            onClick={callback}
          >
            <span className="me-[13px] flex shrink-0 items-center">
              <SquareLogoIcon weight="bold" size={20} />
            </span>
            {translate('Filter by')}
          </MenuPopover.Item>
        </MenuPopover.Content>
      </MenuPopover>
    </span>
  );
});

InlineFilterButton.displayName = 'InlineFilterButton';

const hasFilterMenu = (key: string) => {
  const item = document.querySelector(
    '#kt_content_container [data-slot="table-filters-menu"] #filter-item-' +
      key,
  );
  return Boolean(item);
};

// When a cell's content is wider than its visible width (i.e. the
// `ellipsis` CSS class has truncated it), expose the full value as a
// native browser tooltip on hover. The check runs lazily — only when
// the user actually hovers a cell — so there's no cost on initial
// render. `innerText` is used in preference to `textContent` so
// CSS-hidden affordances (e.g. inline filter buttons) don't leak
// into the tooltip.
const showTruncationTooltip = (e: React.MouseEvent<HTMLTableCellElement>) => {
  const td = e.currentTarget;
  // Prefer the inner `.td-data` element when present: in the first column of an
  // expandable table the ellipsis truncation lives on `.td-data` while the `td`
  // itself does not overflow, so checking only the `td` would miss it.
  const inner = td.querySelector('.td-data') as HTMLElement | null;
  const source = inner ?? td;
  // +1 px tolerance avoids sub-pixel false positives reported by some
  // browsers/zoom levels.
  if (source.scrollWidth > source.clientWidth + 1) {
    const text = (source.innerText || '').trim();
    if (text && td.getAttribute('title') !== text) {
      td.setAttribute('title', text);
    }
  } else if (td.hasAttribute('title')) {
    td.removeAttribute('title');
  }
};

const TableCell = memo(
  ({
    column,
    row,
    isFirstColumn,
    expander,
    hasLeadingCheckbox,
    pinned,
    shadow,
    pinOffset,
  }: {
    column: Column;
    row;
    isFirstColumn?: boolean;
    expander?: RowExpander;
    hasLeadingCheckbox?: boolean;
    pinned?: boolean;
    shadow?: boolean | 'start' | 'end';
    pinOffset?: { left: number; right: number };
  }) => {
    // Derived from the row's details id rather than useId: only the first
    // cell of an expandable row uses them, and a hook per cell is wasted on
    // every other cell of every table.
    const labelId = expander ? `${expander.detailsId}-label` : undefined;
    const toggleTextId = expander ? `${expander.detailsId}-toggle` : undefined;

    // Skip rendering if column is not visible
    if (column.visible === false) {
      return null;
    }

    if (!column.render || typeof column.render !== 'function') {
      return null;
    }

    const renderedContent = React.createElement(column.render, {
      row,
    });

    if (renderedContent === undefined || renderedContent === null) {
      return null;
    }
    const valueToCopy = column.copyField ? column.copyField(row) : '';
    const hasFilter = column.inlineFilter && hasFilterMenu(column.filter);

    const ellipsisEnabled = column.ellipsis ?? true;
    const cellClassName = classNames(
      column.className,
      column.inlineFilter && 'has-filter',
      ellipsisEnabled && 'ellipsis',
      pinned && 'pinned',
      pinned && shadow === 'end' && 'shadow-end',
      pinned && shadow === 'start' && 'shadow-start',
    );
    const pinStyle =
      pinned && pinOffset
        ? { left: pinOffset.left, right: pinOffset.right }
        : undefined;
    const onCellMouseEnter = ellipsisEnabled
      ? showTruncationTooltip
      : undefined;

    // Nothing to copy (a dash, "not assigned"): no copy button either.
    const hasCopy =
      Boolean(column.copyField) && valueToCopy !== '' && valueToCopy != null;
    // The expander is named after this cell's own text ("Expand Web
    // server"). The id goes on `.td-data` when the cell has one, so the copy
    // and filter buttons beside the text stay out of the name.
    const hasExpander = isFirstColumn && expander?.canExpand;
    const dataId = hasExpander && (hasCopy || hasFilter) ? labelId : undefined;
    const content = hasCopy ? (
      <>
        <div className="with-copy flex items-center gap-1">
          <div className="td-data" id={dataId}>
            {renderedContent}
          </div>
          <CopyToClipboardButton value={valueToCopy} />
        </div>
        {hasFilter && <InlineFilterButton column={column} row={row} />}
      </>
    ) : (
      <>
        {hasFilter ? (
          <div className="td-data" id={dataId}>
            {renderedContent}
          </div>
        ) : (
          renderedContent
        )}
        {hasFilter && <InlineFilterButton column={column} row={row} />}
      </>
    );

    const handleClick = column.disabledClick
      ? (e: React.MouseEvent) => e.stopPropagation()
      : undefined;

    // First data column: render expander icon and text in the same cell.
    if (isFirstColumn && expander) {
      return (
        <td
          className={cellClassName}
          onClick={handleClick}
          onMouseEnter={onCellMouseEnter}
          style={{ paddingLeft: 0, paddingRight: 0, ...pinStyle }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              paddingLeft: hasLeadingCheckbox ? 12 : 16,
              paddingRight: 16,
              columnGap: 12,
            }}
          >
            {expander.canExpand && (
              // A real button, so the row opens from the keyboard too. A
              // click on it is ignored by the row's own handler (which skips
              // buttons) and toggles here instead; a click anywhere else on
              // the row still toggles it as before.
              <button
                type="button"
                data-testid="row-expander"
                className={classNames(
                  'inline-flex size-[24px] shrink-0 items-center justify-center rounded border-0 bg-transparent p-0 text-inherit',
                  { active: expander.isExpanded },
                )}
                aria-expanded={expander.isExpanded}
                aria-controls={
                  expander.isExpanded ? expander.detailsId : undefined
                }
                aria-labelledby={`${toggleTextId} ${labelId}`}
                onClick={expander.onToggle}
              >
                <span id={toggleTextId} className="sr-only">
                  {expander.isExpanded
                    ? translate('Collapse')
                    : translate('Expand')}
                </span>
                <CaretDownIcon
                  size={20}
                  weight="bold"
                  className="rotate-toggle-180"
                  aria-hidden="true"
                />
              </button>
            )}
            <div
              id={hasExpander && !dataId ? labelId : undefined}
              style={{ minWidth: 0, flex: '1 1 auto' }}
            >
              {content}
            </div>
          </div>
        </td>
      );
    }

    return (
      <td
        className={cellClassName}
        onClick={handleClick}
        onMouseEnter={onCellMouseEnter}
        style={pinStyle}
      >
        {content}
      </td>
    );
  },
);

TableCell.displayName = 'TableCell';

const TableCells = memo(
  ({
    row,
    columns,
    columnsMap,
    columnPositions,
    hasOptionalColumns,
    pinnedColumns,
    pinnedOffsets,
    expander,
    hasLeadingCheckbox,
  }: TableCellsProps) => {
    const firstVisibleIndex = hasOptionalColumns
      ? columnPositions
          .filter((id) => columnsMap[id])
          .findIndex((id) => columnsMap[id].visible !== false)
      : columns.findIndex((col) => col.visible !== false);

    return (
      <>
        {hasOptionalColumns
          ? columnPositions
              .filter((id) => columnsMap[id])
              .map((id, index) => (
                <Fragment key={id}>
                  <TableCell
                    column={columnsMap[id]}
                    row={row}
                    isFirstColumn={index === firstVisibleIndex}
                    expander={expander}
                    hasLeadingCheckbox={hasLeadingCheckbox}
                    pinned={id in pinnedColumns}
                    shadow={pinnedColumns[id]}
                    pinOffset={pinnedOffsets[id]}
                  />
                </Fragment>
              ))
          : columns.map((column, colIndex) => {
              const pinKey = getColumnPinKey(column, colIndex);
              return (
                <Fragment key={colIndex}>
                  <TableCell
                    column={column}
                    row={row}
                    isFirstColumn={colIndex === firstVisibleIndex}
                    expander={expander}
                    hasLeadingCheckbox={hasLeadingCheckbox}
                    pinned={pinKey in pinnedColumns}
                    shadow={pinnedColumns[pinKey]}
                    pinOffset={pinnedOffsets[pinKey]}
                  />
                </Fragment>
              );
            })}
      </>
    );
  },
);

TableCells.displayName = 'TableCells';

interface TableRowProps {
  row;
  rowIndex: number;
  rowKey: string;
  rowClass: TableProps['rowClass'];
  expandableRow: TableProps['expandableRow'];
  toggled: TableProps['toggled'];
  fieldType: TableProps['fieldType'];
  enableMultiSelect: TableProps['enableMultiSelect'];
  selectRow: TableProps['selectRow'];
  selectedRows: TableProps['selectedRows'];
  rowActions: TableProps['rowActions'];
  fetch: TableProps['fetch'];
  columns: TableProps['columns'];
  columnsMap: Record<string, Column>;
  columnPositions: TableProps['columnPositions'];
  hasOptionalColumns: TableProps['hasOptionalColumns'];
  pinnedColumns: PinnedColumns;
  pinnedOffsets: PinnedOffsets;
  onRowClick: (row, index: number) => void;
  onChangeField: (row, input) => void;
  fieldProps?: { input; meta };
  rowError?: string | string[];
  isRowExpandable?: (row: any) => boolean;
  /** Id of this row's details cell, for the expander's aria-controls. */
  detailsId?: string;
}

const TableRow = memo<TableRowProps>(
  ({
    row,
    rowIndex,
    rowKey,
    rowClass,
    expandableRow,
    toggled,
    fieldType,
    enableMultiSelect,
    selectRow,
    selectedRows,
    rowActions,
    fetch,
    columns,
    columnsMap,
    columnPositions,
    hasOptionalColumns,
    pinnedColumns,
    pinnedOffsets,
    onRowClick,
    onChangeField,
    fieldProps,
    rowError,
    isRowExpandable,
    detailsId,
  }) => {
    const isRowSelected = useMemo(() => {
      if (!selectedRows) return false;
      return selectedRows.some((item) => item[rowKey] === row[rowKey]);
    }, [selectedRows, rowKey, row]);

    let isChecked = false;
    if (fieldProps) {
      if (Array.isArray(fieldProps.input.value)) {
        isChecked = fieldProps.input.value.some(
          (v) => v[rowKey] === row[rowKey],
        );
      } else {
        isChecked = fieldProps.input.value?.[rowKey] === row[rowKey];
      }
    } else {
      isChecked = isRowSelected;
    }

    const handleRowClick = useCallback(
      (event: React.MouseEvent) => {
        // prevent checkbox and expandable row to toggle when clicking on inner clickable elements
        const el = event.target as HTMLElement;
        if (
          el.onclick ||
          el instanceof HTMLInputElement ||
          el.closest('button, a')
        )
          return;

        onRowClick(row, rowIndex);
        if (fieldProps && !expandableRow) {
          onChangeField(row, fieldProps.input);
        }
      },
      [row, rowIndex, fieldProps, expandableRow, onRowClick, onChangeField],
    );

    const handleSelectRow = useCallback(() => {
      selectRow(row);
    }, [selectRow, row]);

    const handleFieldChange = useCallback(() => {
      if (fieldProps) {
        onChangeField(row, fieldProps.input);
      }
    }, [fieldProps, row, onChangeField]);

    const canExpand = expandableRow && (isRowExpandable?.(row) ?? true);
    const isExpanded = Boolean(canExpand && toggled[getId(row, rowIndex)]);
    const toggleExpanded = useCallback(
      () => onRowClick(row, rowIndex),
      [onRowClick, row, rowIndex],
    );
    const expander = useMemo(
      () =>
        expandableRow
          ? {
              canExpand: Boolean(canExpand),
              isExpanded,
              onToggle: toggleExpanded,
              detailsId,
            }
          : undefined,
      [expandableRow, canExpand, isExpanded, toggleExpanded, detailsId],
    );

    return (
      <tr
        className={
          classNames(
            typeof rowClass === 'function' ? rowClass({ row }) : rowClass,
            {
              expanded: canExpand && toggled[getId(row, rowIndex)],
              checked: fieldType && isChecked,
            },
          ) || undefined
        }
        onClick={handleRowClick}
      >
        {(enableMultiSelect || fieldType) && (
          <td className="row-control" style={{ paddingLeft: '16px' }}>
            <div>
              {fieldType && fieldProps ? (
                <>
                  {rowError ? (
                    <Tooltip label={<FieldErrorMessage error={rowError} />}>
                      <WarningCircleIcon
                        weight="bold"
                        size={16}
                        className="error-mark"
                      />
                    </Tooltip>
                  ) : (
                    isChecked &&
                    fieldProps.meta.touched &&
                    fieldProps.meta.error && (
                      <Tooltip label={fieldProps.meta.error}>
                        <WarningCircleIcon
                          weight="bold"
                          size={16}
                          className="error-mark"
                        />
                      </Tooltip>
                    )
                  )}
                  {fieldType === 'radio' ? (
                    <Radio
                      name={fieldProps.input.name}
                      aria-label={translate('Select row')}
                      checked={isChecked}
                      onChange={handleFieldChange}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <Checkbox
                      name={fieldProps.input.name}
                      aria-label={translate('Select row')}
                      checked={isChecked}
                      onChange={handleFieldChange}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </>
              ) : (
                <Checkbox
                  aria-label={translate('Select row')}
                  checked={isChecked}
                  onChange={handleSelectRow}
                />
              )}
            </div>
          </td>
        )}
        <TableCells
          row={row}
          columns={columns}
          columnsMap={columnsMap}
          columnPositions={columnPositions}
          hasOptionalColumns={hasOptionalColumns}
          pinnedColumns={pinnedColumns}
          pinnedOffsets={pinnedOffsets}
          expander={expander}
          hasLeadingCheckbox={Boolean(enableMultiSelect || fieldType)}
        />

        {rowActions && (
          <td
            className={classNames(
              'row-actions',
              COLUMN_ACTIONS_KEY in pinnedColumns && 'pinned pinned-end',
              pinnedColumns[COLUMN_ACTIONS_KEY] && 'is-floating',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div>
              <RowActionsCell rowActions={rowActions} row={row} fetch={fetch} />
            </div>
          </td>
        )}
      </tr>
    );
  },
  // Custom comparison to prevent re-renders when only rowActions changes
  // rowActions is a function that's often recreated on parent renders
  // but doesn't affect the row's visual rendering
  (prevProps, nextProps) => {
    // Compare all props except rowActions
    const { rowActions: _prevRowActions, ...prevRest } = prevProps;
    const { rowActions: _nextRowActions, ...nextRest } = nextProps;

    // Shallow compare remaining props
    const keys = Object.keys(prevRest) as (keyof typeof prevRest)[];
    return keys.every((key) => prevRest[key] === nextRest[key]);
  },
);

TableRow.displayName = 'TableRow';

// Renders a single TableRow inside a react-final-form Field's render prop.
// useFormState is only legal here because this component is mounted inside
// <Field>, which itself lives inside <Form>.
interface FieldRowBodyProps {
  row: any;
  rowIndex: number;
  input: any;
  meta: any;
  rowValidate?: TableProps['rowValidate'];
  renderRow: (
    row: any,
    rowIndex: number,
    fieldProps?: { input; meta } | null,
    rowError?: string | string[],
  ) => JSX.Element;
}
const FieldRowBody: FunctionComponent<FieldRowBodyProps> = ({
  row,
  rowIndex,
  input,
  meta,
  rowValidate,
  renderRow,
}) => {
  const { values } = useFormState({ subscription: { values: true } });
  const rawError = rowValidate ? rowValidate(row, values) : undefined;
  const rowError =
    Array.isArray(rawError) && rawError.length === 0 ? undefined : rawError;
  return renderRow(row, rowIndex, { input, meta }, rowError || undefined);
};

export const TableBody: FunctionComponent<TableBodyProps> = memo(
  ({
    rows,
    columns,
    rowClass,
    rowKey = 'uuid',
    expandableRow,
    expandableRowClassName,
    rowActions,
    onRowClick: onRowClickProp,
    enableMultiSelect,
    selectRow,
    selectedRows,
    toggleRow,
    toggled,
    fetch,
    fieldType,
    fieldName,
    validate,
    rowValidate,
    columnPositions,
    hasOptionalColumns,
    pinnedColumns = {},
    pinnedOffsets = {},
    isRowExpandable,
  }) => {
    // Several tables can share a page, so each prefixes its details ids.
    const detailsIdPrefix = useId();
    const columnsMap = useMemo(
      () =>
        columns.reduce(
          (result, column) => ({ ...result, [column.id]: column }),
          {} as Record<string, Column>,
        ),
      [columns],
    );

    const onRowClick = useCallback(
      (row, index: number) => {
        // Opt-in row-click handler (e.g. master/detail selection) fires first;
        // the inline expand toggle only runs when an expandable row is set.
        onRowClickProp?.(row);
        if (!expandableRow) return;
        toggleRow(getId(row, index));
      },
      [onRowClickProp, expandableRow, toggleRow],
    );

    const onChangeField = useCallback(
      (row, input) => {
        if (fieldType === 'checkbox') {
          const newValues: any[] = input.value || [];
          const index = newValues.findIndex((v) => v[rowKey] === row[rowKey]);
          // Is field checked
          if (index > -1) {
            newValues.splice(index, 1);
          } else {
            newValues.push(row);
          }
          input.onChange(newValues);
        } else if (fieldType === 'radio') {
          input.onChange(row);
        }
        input.onBlur();
      },
      [fieldType, rowKey],
    );

    const renderRow = useCallback(
      (
        row,
        rowIndex: number,
        fieldProps = null,
        rowError: string | string[] | undefined = undefined,
      ) => (
        <TableRow
          row={row}
          rowIndex={rowIndex}
          rowKey={rowKey}
          rowClass={rowClass}
          expandableRow={expandableRow}
          toggled={toggled}
          fieldType={fieldType}
          enableMultiSelect={enableMultiSelect}
          selectRow={selectRow}
          selectedRows={selectedRows}
          rowActions={rowActions}
          fetch={fetch}
          columns={columns}
          columnsMap={columnsMap}
          columnPositions={columnPositions}
          hasOptionalColumns={hasOptionalColumns}
          pinnedColumns={pinnedColumns}
          pinnedOffsets={pinnedOffsets}
          onRowClick={onRowClick}
          onChangeField={onChangeField}
          fieldProps={fieldProps}
          rowError={rowError}
          isRowExpandable={isRowExpandable}
          detailsId={`${detailsIdPrefix}-details-${rowIndex}`}
        />
      ),
      // Note: rowActions is intentionally excluded from dependencies
      // TableRow has a custom comparison function that ignores rowActions changes
      // to prevent unnecessary re-renders when parent creates new rowActions functions
      [
        rowKey,
        rowClass,
        expandableRow,
        toggled,
        fieldType,
        enableMultiSelect,
        selectRow,
        selectedRows,
        fetch,
        columns,
        columnsMap,
        columnPositions,
        hasOptionalColumns,
        pinnedColumns,
        pinnedOffsets,
        onRowClick,
        onChangeField,
        detailsIdPrefix,
      ],
    );

    return (
      <tbody>
        {rows.map((row, rowIndex) => {
          const canExpand = expandableRow && (isRowExpandable?.(row) ?? true);
          return (
            <React.Fragment key={rowIndex}>
              {fieldType ? (
                <Field name={fieldName} validate={validate as any}>
                  {({ input, meta }) => (
                    <FieldRowBody
                      row={row}
                      rowIndex={rowIndex}
                      input={input}
                      meta={meta}
                      rowValidate={rowValidate}
                      renderRow={renderRow}
                    />
                  )}
                </Field>
              ) : (
                renderRow(row, rowIndex)
              )}
              {canExpand && toggled[getId(row, rowIndex)] && (
                <tr>
                  <td
                    colSpan={
                      columns.length +
                      (rowActions ? 1 : 0) +
                      (enableMultiSelect || fieldType ? 1 : 0)
                    }
                    id={`${detailsIdPrefix}-details-${rowIndex}`}
                    className={expandableRowClassName}
                    data-testid="expanded-row-cell"
                  >
                    {React.createElement(expandableRow, { row, fetch })}
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    );
  },
);

(TableBody as FunctionComponent).displayName = 'TableBody';
